import { Subscription, noop } from 'rxjs';
import sortBy from 'lodash/sortBy';
import { tap, take } from 'rxjs/operators';
import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  HostBinding,
  ViewChild,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ShareTrackingURLModalComponent } from '../../modals/share-tracking-modal/share-tracking-modal.component';
import { MapComponent } from '../../map/map.component';
import { DispatchTrackingService, IDispatch, IDispatchOrderDetails, IDispatchStop } from './dispatch-tracking.service';
import { IInfoWindow, IPixelOffset, MapMarkerDirective } from '../../map/marker.directive';

import { GREEN_40, WHITE } from '@app/lib/colors';

const THUMB_HEIGHT = 22;
const SUMMARY_HEIGHT = 156;
const MAIN_HEADER_HEIGHT = 64;
const ANIMATE_TRANSITE = 'top 0.5s ease 0s';
const IN_ANIMATE_TRANSITE = 'none';

const SIDEPANE_HIGHEST = window.innerHeight - (THUMB_HEIGHT + SUMMARY_HEIGHT);
const SIDEPANE_LOWEST = MAIN_HEADER_HEIGHT - THUMB_HEIGHT;

@Component({
  templateUrl: './dispatch-tracking.component.html',
  styleUrls: ['./dispatch-tracking.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DispatchTrackingComponent implements OnInit, OnDestroy {
  @ViewChild('shareTrackingModal', { static: true }) public shareTrackingModal: ShareTrackingURLModalComponent;
  @ViewChildren(MapMarkerDirective) public mapMarkers: QueryList<MapMarkerDirective>;
  @ViewChild('mapComponent', { static: false }) set content(content: MapComponent) {
    if (content) {
      this.map = content;
      Promise.all(this.map.markerPromiseList).then(this.assignTooltips.bind(this));
    }
  }
  public map: MapComponent;
  @HostBinding('class.hide-map-view') public hideMapView: boolean = false;
  @HostBinding('class.page-not-found') public isDispatchNotFound: boolean;
  public isLoading: boolean = true;
  public green40 = GREEN_40;
  public isDispatchDeparted = this.dispatchTrackingService.isDispatchDeparted;
  public label = {
    color: 'white',
    fontSize: '14px',
    text: '1',
  };
  public mapOptions: google.maps.MapOptions = {
    gestureHandling: 'none',
    disableDefaultUI: true,
    clickableIcons: false,
    mapTypeControl: false,
    fullscreenControl: false,
    zoomControl: false,
    scrollwheel: false,
    disableDoubleClickZoom: false,
    draggable: false,
    panControl: false,
    keyboardShortcuts: false,
    tilt: 0,
  };
  public currentTopPosition: number;
  public sidePaneTransition: string;
  public dispatchDetails: IDispatch;
  private dispatchDetailSubscription: Subscription;

  public get hideDot(): boolean {
    const singleLineMaxLength = 29;
    return this.dispatchDetails.dispatchFrom.length + this.dispatchDetails.dispatchTo.length > singleLineMaxLength;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private dispatchTrackingService: DispatchTrackingService,
    private titleService: Title,
  ) {
    this.titleService.setTitle($localize`Dispatch tracking`);
  }

  public ngOnInit(): void {

    // TODO: Remove following snippet after url formating BE changes come in place
    // window['publicDispatchData'] is assigned before bootstraping app in boot.ts
    const weakDataSource: {iv: string, token: string} = window['publicDispatchData'];
    if (!this.route.snapshot.queryParamMap.get('token') && weakDataSource) {
      this.router.navigate(
        [],
        {
          relativeTo: this.route,
          queryParams: weakDataSource,
          queryParamsHandling: 'merge',
        });
    }

    const token = this.route.snapshot.queryParamMap.get('token') || weakDataSource.token;
    const iv = this.route.snapshot.queryParamMap.get('iv') || weakDataSource.iv;
    const dispatchShareEnabled = this.route.snapshot.queryParamMap.get('shareDispatch') === 'true';
    window['publicDispatchData'] = undefined;
    this.dispatchDetailSubscription = this.dispatchTrackingService.getDispatchDetails(token, iv)
      .pipe(
        tap((dispatchDetails: IDispatch) => {
          this.hideMapView = dispatchDetails.status === 'completed' ? true : false;
          this.isDispatchNotFound = (dispatchDetails.status === 'pending' || dispatchDetails.status === 'planned' ||
            dispatchDetails.status === 'cancelled') ? true : false;
        }),
      )
      .subscribe(
        (dispatchDetails: IDispatch) => {
          this.dispatchDetails = dispatchDetails;
          this.titleService.setTitle($localize`Dispatch #${this.dispatchDetails.orderNumber}`);
          if (dispatchShareEnabled) {
            window.location.href = window.location.href.substr(0, window.location.href.indexOf('shareDispatch') - 1);
            this.onShareClick();
          }
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        (err: Error) => {
          console.error(err);
          this.isDispatchNotFound = true;
          this.isLoading = false;
          this.cdr.markForCheck();
        });
  }

  public ngOnDestroy(): void {
    this.dispatchDetailSubscription.unsubscribe();
  }

  public isProductDetailExists(dispatchOrderDetails: IDispatchOrderDetails): boolean {
    return !!(dispatchOrderDetails.product || dispatchOrderDetails.pieces || dispatchOrderDetails.weight ||
      dispatchOrderDetails.volume);
  }

  public isDispatchArrivedAtFirstStop(dispatchDetails): boolean{
    const sortedDispatches = sortBy(dispatchDetails.dispatchStops, a => a.number);
    return !!sortedDispatches[0].arrivalDate;
  }

  public getMapMarkerLabel(dispatchStop: IDispatchStop): string {
    if (dispatchStop.isLastKnownLocation) return 'dispatch-unknown';
    else if (dispatchStop.status === 'arrivalPending' || dispatchStop.status === 'available') return 'dispatch-to-pick';
    else if (dispatchStop.status === 'arrived' || dispatchStop.status === 'in_progress') return 'dispatch-picking';
    else if (dispatchStop.status === 'departed') return 'dispatch-picked';
  }

  public getMapMarkerLabelText(dispatchStop: IDispatchStop): google.maps.MarkerLabel {
    if (dispatchStop.isLastKnownLocation) return null;
    return {
      text: dispatchStop.number.toString(),
      color: WHITE,
      fontSize: '18px',
      fontWeight: '400',
    };
  }

  public getMapMarkerInfo(dispatchStop: IDispatchStop): IInfoWindow {
    if (dispatchStop.isLastKnownLocation) return;
    let content: IInfoWindow['content'];
    const dispatchLocation: IDispatchStop['dispatchLocation'] = dispatchStop.dispatchLocation;
    if (dispatchLocation.name && dispatchLocation.address1) {
      content = `<div class="title">${dispatchLocation.name}</div>${dispatchLocation.address1},
      ${dispatchLocation.city}, ${dispatchLocation.state}`;
    } else {
      content = `${dispatchLocation.city}, ${dispatchLocation.state}`;
    }
    const offsetInProgress = 148;
    const offsetPickedOrToPicked = 126;
    return {
      content,
      verticalOffset: dispatchStop.status === 'arrived' ? offsetInProgress : offsetPickedOrToPicked,
    };
  }

  public getShipperStops(numberOfStops: number): string {
    // tslint:disable-next-line: max-line-length
    return (numberOfStops > 1) ? $localize`${numberOfStops} stops` : $localize`${numberOfStops} stop`;
  }

  public get isDispatchExpanded(): boolean {
    return this.currentTopPosition === SIDEPANE_LOWEST;
  }

  public onPane(event): void {
    const panPoint = event.center.y;
    if (panPoint <= SIDEPANE_HIGHEST) this.currentTopPosition = panPoint;
    else this.currentTopPosition = SIDEPANE_HIGHEST;
    this.cdr.markForCheck();
  }

  public onPanEnd(event): void {
    this.sidePaneTransition = ANIMATE_TRANSITE;
    setTimeout(() => this.sidePaneTransition = IN_ANIMATE_TRANSITE, 1000);
    const panEndValue = event.center.y;
    const distanceFromTop = Math.abs(SIDEPANE_LOWEST - panEndValue);
    const distanceFromBottom = Math.abs(panEndValue - SIDEPANE_HIGHEST);
    if (distanceFromTop < distanceFromBottom) {
      this.currentTopPosition = SIDEPANE_LOWEST;
    } else {
      this.currentTopPosition = SIDEPANE_HIGHEST;
    }
    this.cdr.markForCheck();
  }

  public onShareClick(): void {
    this.shareTrackingModal
      .show({ trackingURL: window.location.href, id: this.dispatchDetails.id, orderNumber: this.dispatchDetails.orderNumber });
  }

  public onShareButtonClicked(email: string): void {
    this.dispatchTrackingService.reshareDispatchURL(email)
      .pipe(this.shareTrackingModal.handleEmailSentError())
      .subscribe((emailSent: boolean) => {
        this.shareTrackingModal.handleEmailSentResponse(emailSent);
      });
  }

  private assignTooltips(): void {
    this.map.tilesLoaded$()
      .pipe(take(1))
      .subscribe(() => {
        this.mapMarkers.toArray().filter(marker => marker.type !== 'dispatch-unknown').forEach(marker => {
          this.assignTooltipToMarker(marker);
        });
      });
  }

  private assignTooltipToMarker(marker: MapMarkerDirective): void {
    const { pixelOffsetX, pixelOffsetY } = this.calculateTooltipPixelOffset(marker);
    const infoWindow: google.maps.InfoWindow = new google.maps.InfoWindow({
      content: marker.info.content,
      pixelOffset: new google.maps.Size(pixelOffsetX, pixelOffsetY),
      disableAutoPan: true,
    });
    marker.markerObj.addListener('mouseover', () => this.openWindow(infoWindow, marker.markerObj));
    marker.markerObj.addListener('mouseout', () => infoWindow.close());
  }

  private openWindow(infoWindow: google.maps.InfoWindow, markerObj: google.maps.Marker): void {
    const mapObj: MapComponent = this.map;
    infoWindow.open(mapObj.map, markerObj);
  }

  private calculateTooltipPixelOffset(marker: MapMarkerDirective): IPixelOffset {
    const mapObj: google.maps.Map = this.map.map;
    const point: google.maps.Point = this.getPixelPositionOfMarker(mapObj, marker.markerObj);
    const widthOfMap = mapObj.getDiv().clientWidth;
    const heightOfMap = mapObj.getDiv().clientHeight;
    const tooltipMaxHeight = 86;
    const tooltipMaxWidth = 196;
    const offset = 50;
    const distanceFromBottom = heightOfMap - point.y;
    const canDirectBottom = distanceFromBottom > (tooltipMaxHeight + offset);
    const widthOffsetOfTooltip = tooltipMaxWidth + offset;
    const halfOffsetWidthOfTooltip = widthOffsetOfTooltip / 2;
    const canCenterAligned = point.x > halfOffsetWidthOfTooltip
      && (widthOfMap - point.x) > halfOffsetWidthOfTooltip;  // leftOffset + tooltipWidth  + rightOffset
    const canDirectLeft = point.x > widthOffsetOfTooltip;
    const canDirectRight = (widthOfMap - point.x) > widthOffsetOfTooltip;
    let pixelOffsetX = 0;
    let pixelOffsetY = 0;
    if (canCenterAligned) {
      if (canDirectBottom) pixelOffsetY = marker.info.verticalOffset;
      else pixelOffsetY -= this.calculateTooltipOverflowHeight(marker);
    } else if (canDirectLeft) {
      pixelOffsetX = - widthOffsetOfTooltip / 2;
      pixelOffsetY = marker.info.verticalOffset / 2;
    } else if (canDirectRight) {
      pixelOffsetX = widthOffsetOfTooltip / 2;
      pixelOffsetY = marker.info.verticalOffset / 2;
    }
    if (canDirectBottom || canDirectLeft || canDirectRight) pixelOffsetY += this.calculateTooltipOverflowHeight(marker);
    return { pixelOffsetX, pixelOffsetY };
  }

  private calculateTooltipOverflowHeight(marker: MapMarkerDirective): number {
    const infoWindowDummyDiv: HTMLElement = document.createElement('div');
    infoWindowDummyDiv.classList.add('gm-style-iw-container');
    infoWindowDummyDiv.innerHTML = marker.info.content
      .replace('class="title"', 'class="gm-style-iw-title"');
    document.body.appendChild(infoWindowDummyDiv);
    // Formula: ((height of tooltip div / height of each row of tooltip div) - height of first two rows) * new extra height)
    const multiplyFactor: number = ((infoWindowDummyDiv.offsetHeight / 17) - 2);
    const offsetHeight: number = multiplyFactor >= 1 ? multiplyFactor * 13 : 0;
    document.body.removeChild(infoWindowDummyDiv);
    return offsetHeight;
  }

  private getPixelPositionOfMarker(map: google.maps.Map, marker: google.maps.Marker): google.maps.Point {
    const overlay = new google.maps.OverlayView();
    overlay.draw = noop;
    overlay.setMap(map);
    const proj: google.maps.MapCanvasProjection = overlay.getProjection();
    const pos: google.maps.LatLng = marker.getPosition();
    return proj.fromLatLngToContainerPixel(pos);
  }
}
